import { BackLink } from '@/components/BackLink';
import { Button } from '@/components/Button';
import { KioskCard } from '@/components/KioskCard';
import { KioskScreen } from '@/components/KioskScreen';
import { StatusBadge } from '@/components/StatusBadge';
import { Stepper } from '@/components/Stepper';
import { JOURNEY_STEP } from '@/components/stepper.steps';
import type { Translate } from '@/i18n/useTranslation';
import { useTranslation } from '@/i18n/useTranslation';
import { captureCandidateSession, SCREEN, useKioskSession } from '@/lib/kioskSession';
import type { SubmissionOutcome } from '@/services/submission';
import { createSubmission } from '@/services/submission';
import { useFocusOnMount } from '@/shared/hooks/useFocusOnMount';

import { shiftLine } from '../shift';
import type { JobDetail } from '../types';
import { JobDescription } from './JobDescription';
import type { SummaryRow } from './JobSummaryRows';
import { JobSummaryRows } from './JobSummaryRows';
import { NoJobsScreen } from './NoJobsScreen';

/**
 * One opening, opened [KJM-3; Figma `08 · Job detail` — 12021:40798 desktop · 40773 iPad · 40749 mobile, all
 * three read 2026-08-21].
 *
 * **It fetches nothing.** The published description arrived with the list — one read per screen, never a
 * poll [decision 0029] — so opening a card is a state change and not a request. That is why there is no
 * loading state here and no error state either.
 *
 * **The card presents; it never decides** [KJM-3 § Invariant]. `featured` puts a badge above the title and
 * changes nothing else, because the backend already resolved the pin against the radius-filtered set.
 */
export function JobDetailScreen() {
  const t = useTranslation();
  const heading = useFocusOnMount<HTMLHeadingElement>();
  const job = useKioskSession((state) => state.selectedJob);
  const goTo = useKioskSession((state) => state.goTo);
  const candidateId = useKioskSession((state) => state.lookupResult?.candidateId);
  const setSubmission = useKioskSession((state) => state.setSubmission);
  const beginSubmission = useKioskSession((state) => state.beginSubmission);
  const settleSubmission = useKioskSession((state) => state.settleSubmission);
  const markSubmissionFailed = useKioskSession((state) => state.markSubmissionFailed);
  /*
   * Read from the store, not from local state [0186; ISS-223]. The header Back stays enabled while the
   * write is in flight, so this screen can unmount and come back on the same job before it settles. A local
   * flag came back "idle", and the second tap sent a second submission for one job order.
   */
  const submitting = useKioskSession(
    (state) => job !== undefined && state.submittingJobOrderIds.includes(job.jobOrderId),
  );

  /**
   * Creates the submission, then moves on — in that order.
   *
   * **The screen advances either way**, and that is BR-K7 rather than optimism: a candidate at an unattended
   * kiosk must never be held on a screen because a write failed. What changes is where they land. With a
   * submission, `FinishScreen` shows their next steps; without one it shows the representative hand-off,
   * which is the honest outcome — the system does not pretend a record exists.
   */
  async function select(): Promise<void> {
    // Checked and marked in one step on the store, so neither a double tap nor a re-tap after Back and
    // reopen can send a second write for a job order whose first is still in flight.
    if (job === undefined || !beginSubmission(job.jobOrderId)) return;

    /*
     * No candidate id means there is no Bullhorn record to attach a submission to — KAP-7 creates the
     * candidate and that half is not built. The write is SKIPPED rather than attempted, because posting
     * anyway would create an orphan in ISS's system of record.
     *
     * **The candidate carries on regardless** [BR-K7]. They still answer the fit checks, and a recruiter
     * still gets their profile and their answers; what is missing is the submission row, which is a gap
     * for an operator to see and not a reason to stop somebody at a machine. Sending them to the
     * representative hand-off here — which is what this used to do — ended the new-candidate flow three
     * screens early, on the one path that has no record yet by definition.
     */
    const stillTheirs = captureCandidateSession();
    const outcome =
      candidateId === undefined
        ? ({ kind: 'unreachable' } as const)
        : await createSubmission(candidateId, job.jobOrderId);
    /*
     * ⛔ **A submission that lands after Exit is dropped, whatever it says** [KID-1, KID-2: one visit, one
     * person] — the same rule as the profile page. Applied after Exit and YES, it put this person's
     * submission into the next person's session, where `FinishScreen` would show them next steps for an
     * application they never made, and it took them to the fit checks of a job they never opened. A
     * `created` submission stays in Bullhorn either way [0073: it is written on the tap]; nothing on this
     * device is still waiting to be told about it.
     *
     * It is logged here too, so a write that lands after Exit or a 401 leaves the same trace as one that
     * lands after a header Back. ⚠️ The in-flight flag is NOT cleared on this path. Ending the session
     * already cleared it, and the next candidate may have their own write in flight for this job order now.
     * Clearing it here would let them send a second one.
     */
    if (!stillTheirs()) {
      warnSettledAfterLeave(outcome);
      return;
    }
    settleSubmission(job.jobOrderId);

    /*
     * ⛔ **A write that settles after the candidate left moves nothing.** The interest button and the way back
     * beside it are disabled while the write is in flight. The header Back, Exit and a 401 are not, and any of
     * them can end this screen before the write settles. The store is read again here because the closure
     * still holds the screen as it was at the tap. Moving on from a stale view would pull a candidate who
     * went back to the list into the fit checks. After Exit it would also write this submission into the
     * NEXT candidate's session.
     *
     * Whatever reached Bullhorn stands [0033]; only the kiosk stays where it is now. The log is the trace of
     * that, and it carries no candidate detail because it prints on a shared device's console.
     */
    const now = useKioskSession.getState();
    if (now.screen !== SCREEN.jobDetail || now.selectedJob?.jobOrderId !== job.jobOrderId) {
      warnSettledAfterLeave(outcome);
      return;
    }

    if (outcome.kind === 'created') {
      setSubmission({
        id: outcome.submissionId,
        jobTitle: job.title ?? '',
        clientName: job.clientName ?? '',
      });
    } else if (candidateId !== undefined) {
      // A write was SENT and created nothing: the submission-failed screen replaces the confirmation
      // [KPW-1c]. A skipped write is not a failed one, so it keeps the generic hand-off.
      markSubmissionFailed();
    }
    // The fit checks come next [prototype; KAP-7]. The submission was already written above, on the tap,
    // which is decision 0073 — before the confirmation screen and before any fit question.
    goTo(SCREEN.fitQuestions);
  }

  /**
   * Back to the openings — from the header's Back and from the bottom of the page [decision 0305]. One
   * handler for both, so the two controls cannot drift apart. The candidate session, the language and the
   * list the backend already sent all stay in the store, so the list comes back as it was and is not
   * re-fetched [0029].
   */
  const backToList = (): void => goTo(SCREEN.jobs);

  /*
   * Reached with nothing selected only by a seeded state or a bug. The neutral hand-off rather than a blank
   * card, and rather than a redirect: sending the candidate back to a list that may itself be empty is how a
   * loop happens on a device nobody can close.
   */
  if (job === undefined) return <NoJobsScreen enqueued={false} />;

  return (
    <KioskScreen>
      <KioskCard
        band={<Stepper current={JOURNEY_STEP.findJob} />}
        footer={
          /*
           * **KPW-1 — this tap is the application.** One tap, one Bullhorn JobSubmission, created BEFORE the
           * confirmation screen [0073] and unconditional on any screening question. The status it carries is
           * the trigger of an ISS automation the kiosk cannot observe, so the button reports only what it
           * can know: a submission came back, or one did not.
           *
           * It is disabled while the write is in flight rather than debounced — "one tap, one submission" is
           * an invariant about ISS's system of record, and a candidate double-tapping a slow screen is the
           * ordinary way a second one gets created.
           */
          /*
             The frame HUGS this action and pushes it right from `sm` up — 207x52 against a 553 card at 820
             and against a 768 card at 1440 — and stretches it to the full 343 at 375. Same shape as Finish,
             and it is NOT the check-in footer's full-width button that `KioskCard` documents: measured on
             `12055:10391` / `10416` / `10441` on 2026-09-02. Built full-width it was 720 wide on desktop.
           */
          /*
             ⭐ **The way back to the list sits beside it** [decision 0305]. Once the description is open in
             full, the header's Back can be a long scroll above the candidate, and Jaime Donnelly, reading
             the page as a first-time user, did not see it at all. So the way back is put where the
             description ends: in the footer, beside the interest control.

             Layout read from `New user / 07 · Job detail` at all three widths, live 2026-10-01:
               1440  a row, gap 8, packed right — `Secondary` HUGS (275) · `Primary` HUGS (293)
               820   a row, gap 8 — `Secondary` FILLS what `Primary` leaves (231 of a 532 band)
               375   a column, gap 8 — `Primary` on top, `Secondary` under it, both full width
             DOM order is the row's order, which is also the tablet kiosk's: Secondary, then Primary.
             `flex-col-reverse` stacks Primary on top at 375, the one width where the two orders differ.

             ⚠️ **The label wraps inside its button where one line cannot fit, and it never spills past
             the outline.** QA, 2026-10-05: at 945 the text ran past both edges. Padding and type already
             match the frame's Secondary (24 a side, 16/20 semibold, 0.4 tracking). The row is what is
             short. Beside Primary, Secondary gets about 206 (English) and 220 (Spanish) in a 553 card,
             and about 233 and 247 in the redrawn 580 card. Both are under the label plus its padding
             (277 and 267). The frame fits it at 820 only by running the text 2px from the stroke. So
             from `sm` up to 1439 the label takes two balanced, centred lines, and both buttons grow to
             the same height. At 375 and 1440 it fits on one line, as drawn.

             It goes to the LIST and nowhere else, so it never ends the visit and is not the Exit question.
             It shares one handler with the header's Back, so the two can never disagree about where "back"
             goes. It is disabled while the application is being written, like Primary. A tap there means
             "not this one" in the same moment the candidate said "this one". `select` also refuses to move a
             kiosk the candidate has left, for the exits that are not disabled.
           */
          <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <div className="flex min-w-0 sm:flex-1 min-[1440px]:flex-none">
              <Button disabled={submitting} fullWidth onClick={backToList} variant="outline">
                <span className="text-balance text-center">{t('jobs.detail.lookForAnother')}</span>
              </Button>
            </div>
            <Button
              disabled={submitting}
              onClick={() => {
                // The handler is async and the attribute wants void — the promise is owned by `select`,
                // which never rejects: every failure is turned into a value inside it.
                void select();
              }}
            >
              {t(submitting ? 'jobs.detail.selecting' : 'jobs.detail.select')}
            </Button>
          </div>
        }
        header={
          <>
            <BackLink onClick={backToList} />
            <StatusBadge label={t('badge.kiosk')} />
          </>
        }
      >
        <div className="flex w-full flex-col items-start gap-6 pb-8">
          <div className="flex w-full flex-col items-start gap-1">
            {job.featured && (
              <span className="rounded-full bg-[#fdf1df] px-2 py-[2px] text-[10px] font-bold uppercase leading-3 tracking-[0.2px] text-[#8a5a06]">
                {t('jobs.featured')}
              </span>
            )}
            {/*
              The visible title is the JOB's, which is data rather than copy — job-order content is presented
              as stored, in its source language [§ 7; decision 0026]. `jobs.detail.heading` is the screen's
              own name and is used for the accessible label, so a screen reader announces "Job details" and
              then reads the title, instead of announcing a company's job title as the name of a screen.
            */}
            <h1
              aria-label={t('jobs.detail.heading')}
              className="kiosk-heading-focus w-full text-2xl font-bold leading-7 tracking-[-0.36px] text-black"
              ref={heading}
              tabIndex={-1}
            >
              {job.title ?? t('jobs.untitled')}
            </h1>
            {job.city !== null && (
              <p className="text-sm leading-5 text-[var(--muted-foreground)]">{job.city}</p>
            )}
          </div>

          <JobSummaryRows rows={summaryRows(job, t)} />

          {job.publishedDescription !== null && <JobDescription html={job.publishedDescription} />}

          {/*
            ⭐ **What the tap does, said before it is made** [KJM-3a AC-8; BR-K9; 0003, 0097]. The candidate is
            told that selecting sends an APPLICATION, not that it books a spot, and which contingencies may
            stand between them and a start date. QA, 2026-10-06: without these the detail named neither.

            The two sentences are ISS's own copy, verbatim from `New user / 07 · Job detail`: nodes
            `12441:27917`/`27918` (Desktop), `27919`/`27920` (iPad) and `27922`/`27923` (Mobile), read live
            2026-10-06 [0297; 0040]. The frame draws them as the last two blocks of the content column, after
            the EEO and Use-of-AI notices and before the footer, at 16/24 regular black, 24 apart. That is
            the `gap-6` of this column, so they sit here as its last children.

            They are shown always, and never inside the accordion. The frame draws them with the
            description's body collapsed, and the expectation has to be on screen whether or not this order
            carries a description. The EEO and Use-of-AI notices are not moved: they are part of ISS's
            published description and render inside it [0072; see `JobDescription`].
          */}
          <p className="w-full text-base leading-6 text-black">
            {t('jobs.detail.sendsApplication')}
          </p>
          <p className="w-full text-base leading-6 text-black">{t('jobs.detail.contingencies')}</p>
        </div>
      </KioskCard>
    </KioskScreen>
  );
}

/**
 * The trace of a submission write that settled after the candidate left the job detail, by Back, Exit or a
 * 401. One function for both guards in `select`, so the two traces cannot drift apart. It carries no candidate
 * detail, because it prints on a shared device's console.
 */
function warnSettledAfterLeave(outcome: SubmissionOutcome): void {
  console.warn({
    msg: 'POST /submissions settled after the candidate left the job detail — the kiosk was not moved',
    alert: 'kiosk_submission_settled_after_leave',
    created: outcome.kind === 'created',
  });
}

/**
 * The five rows KJM-3 names, in the frame's order, with the absent ones dropped.
 *
 * `Job ID` is the Bullhorn order id and is always present — it is what a recruiter asks for on the phone, so
 * it is the one row that never disappears.
 */
function summaryRows(job: JobDetail, t: Translate): readonly SummaryRow[] {
  const rows: SummaryRow[] = [];
  const shift = shiftLine(job);

  if (shift !== null) rows.push({ label: t('jobs.detail.shift'), value: shift });
  if (job.payRate !== null) rows.push({ label: t('jobs.detail.pay'), value: job.payRate });
  if (job.jobType !== null) rows.push({ label: t('jobs.detail.jobType'), value: job.jobType });
  if (job.city !== null) rows.push({ label: t('jobs.detail.location'), value: job.city });
  rows.push({ label: t('jobs.detail.jobId'), value: String(job.jobOrderId) });

  return rows;
}
